/**
 * Order history.
 *
 * The screen a manager opens when a customer comes back with a complaint, or
 * when an invoice needs reprinting, or to find out why a bill was voided. So it
 * is built around search and around what happened to each order afterwards -
 * including whether the tax authority accepted it.
 */

import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    FiSearch, FiPrinter, FiXCircle, FiSend, FiFileText, FiEye, FiRefreshCw
} from 'react-icons/fi';
import toast from 'react-hot-toast';

import { orderApi, fiscalApi } from '../services/endpoints';
import useAsync, { useAction } from '../hooks/useAsync';
import DataTable from '../components/ui/DataTable';
import Modal from '../components/ui/Modal';
import { StatusBadge } from '../components/ui/Feedback';
import { Field, Select, Textarea } from '../components/ui/Field';
import Receipt from '../components/print/Receipt';
import { printReceipt } from '../components/print/printBus';
import { money, formatDateTime, toInputDate } from '../utils/format';
import { selectLookups } from '../store/menuSlice';
import { loadFromOrder } from '../store/cartSlice';
import { useNavigate } from 'react-router-dom';

const OrdersPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { voidReasons, orderStatuses, orderTypes } = useSelector(selectLookups);

    const [filters, setFilters] = useState({
        fromDate: toInputDate(new Date()),
        toDate: toInputDate(new Date()),
        statusCode: '',
        orderTypeCode: '',
        search: '',
        page: 1,
        pageSize: 25
    });

    const [receipt, setReceipt] = useState(null);
    const [voidTarget, setVoidTarget] = useState(null);
    const [voidReasonId, setVoidReasonId] = useState('');
    const [voidRemarks, setVoidRemarks] = useState('');

    const load = useCallback(() => orderApi.list(filters), [filters]);
    const { data: orders, meta, loading, refetch } = useAsync(load, [filters]);

    const openReceipt = async (orderId) => {
        try {
            setReceipt(await orderApi.getReceipt(orderId));
        } catch (err) {
            toast.error(err.message);
        }
    };

    const { run: doPrint } = useAction(async (orderId) => {
        const model = await orderApi.getReceipt(orderId);
        await printReceipt(model, { copies: 1 });
        await orderApi.confirmPrint(orderId).catch(() => { /* the print is what mattered */ });
    }, { successMessage: 'Sent to the printer.' });

    const { run: doResubmit } = useAction(
        (orderId) => fiscalApi.submitOrder(orderId),
        { onSuccess: () => refetch() }
    );

    const { run: doVoid, busy: voiding } = useAction(
        (orderId) => orderApi.void(orderId, {
            voidReasonId: voidReasonId ? Number(voidReasonId) : null,
            voidRemarks: voidRemarks.trim() || null
        }),
        {
            successMessage: 'Order voided.',
            onSuccess: () => {
                setVoidTarget(null);
                setVoidReasonId('');
                setVoidRemarks('');
                refetch();
            }
        }
    );

    /** Reopen a held or unpaid order on the till. */
    const resume = async (order) => {
        try {
            const detail = await orderApi.get(order.Order_ID);
            dispatch(loadFromOrder(detail));
            navigate('/pos');
        } catch (err) {
            toast.error(err.message);
        }
    };

    const columns = [
        {
            key: 'Order_Number',
            header: 'Order',
            // The heading of the card on a phone.
            primary: true,
            render: (row) => (
                <div>
                    <p className="font-semibold text-slate-800">{row.Order_Number}</p>
                    {row.Invoice_Number && (
                        <p className="text-[11px] font-medium text-slate-500 mt-0.5">{row.Invoice_Number}</p>
                    )}
                </div>
            )
        },
        {
            key: 'Order_Date_Time',
            header: 'When',
            render: (row) => (
                <span className="text-sm font-medium text-slate-600">{formatDateTime(row.Order_Date_Time)}</span>
            )
        },
        {
            key: 'Order_Type_Name',
            header: 'Type',
            render: (row) => (
                <div>
                    <p className="font-semibold text-slate-700">{row.Order_Type_Name}</p>
                    {row.Table_Number && (
                        <p className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 mt-1 text-[10px] font-bold text-slate-600">
                            Table {row.Table_Number}
                        </p>
                    )}
                </div>
            )
        },
        {
            key: 'Customer_Name',
            header: 'Customer',
            render: (row) => (
                <div className="max-w-[12rem]">
                    <p className="truncate font-medium text-slate-800">{row.Customer_Name || 'Walk-in'}</p>
                    {row.Customer_Mobile && (
                        <p className="truncate text-[11px] font-medium text-slate-500 mt-0.5">{row.Customer_Mobile}</p>
                    )}
                </div>
            )
        },
        {
            key: 'Item_Count',
            header: 'Items',
            align: 'right',
            render: (row) => <span className="font-bold text-slate-500 tabular">{row.Item_Count}</span>
        },
        {
            key: 'Total_Payable',
            header: 'Total',
            align: 'right',
            render: (row) => <span className="font-semibold text-slate-800 tabular">Rs. {money(row.Total_Payable)}</span>
        },
        {
            key: 'Payment_Modes',
            header: 'Paid by',
            // On a card this repeats what the status line already says for
            // every order that is not yet paid.
            mobileHidden: true,
            render: (row) => <span className="text-sm font-semibold text-slate-500">{row.Payment_Modes || '—'}</span>
        },
        {
            key: 'Order_Status_Code',
            header: 'Status',
            render: (row) => <StatusBadge status={row.Order_Status_Code} label={row.Order_Status_Name} />
        },
        {
            key: 'Fiscal_Status_Code',
            header: 'FBR / PRA',
            render: (row) => {
                if (!row.Fiscal_Status_Code) {
                    return <span className="text-[11px] font-medium uppercase tracking-wider text-slate-400">Not Reported</span>;
                }
                return (
                    <div className="space-y-1">
                        <StatusBadge status={row.Fiscal_Status_Code} label={row.Fiscal_Status_Name} />
                        {row.Authority_Invoice_Number && (
                            <p className="tabular max-w-[10rem] truncate text-[10px] font-medium text-slate-400">
                                {row.Authority_Invoice_Number}
                            </p>
                        )}
                    </div>
                );
            }
        },
        {
            key: 'actions',
            header: '',
            // The table puts these under a blank heading; a card line needs a
            // word beside them.
            mobileLabel: 'Actions',
            render: (row) => (
                <div className="flex items-center justify-end gap-1.5 opacity-100 transition-opacity
                                [@media(hover:hover)]:opacity-60 group-hover:opacity-100">
                    <button
                        type="button"
                        onClick={(event) => { event.stopPropagation(); openReceipt(row.Order_ID); }}
                        className="flex h-11 w-11 items-center justify-center rounded-lg lg:h-8 lg:w-8 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                        title="View the receipt"
                    >
                        <FiEye className="h-4 w-4" />
                    </button>

                    {row.Order_Status_Code === 'PAID' && (
                        <button
                            type="button"
                            onClick={(event) => { event.stopPropagation(); doPrint(row.Order_ID); }}
                            className="flex h-11 w-11 items-center justify-center rounded-lg lg:h-8 lg:w-8 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                            title="Reprint"
                        >
                            <FiPrinter className="h-4 w-4" />
                        </button>
                    )}

                    {row.Order_Status_Code === 'PAID'
                        && row.Fiscal_Status_Code
                        && row.Fiscal_Status_Code !== 'ACCEPTED' && (
                            <button
                                type="button"
                                onClick={(event) => { event.stopPropagation(); doResubmit(row.Order_ID); }}
                                className="flex h-11 w-11 items-center justify-center rounded-lg lg:h-8 lg:w-8 text-amber-500 hover:bg-amber-100 hover:text-amber-700 transition-colors"
                                title="Send to the authority now"
                            >
                                <FiSend className="h-4 w-4" />
                            </button>
                        )}

                    {!['VOID', 'REFUNDED'].includes(row.Order_Status_Code) && (
                        <button
                            type="button"
                            onClick={(event) => { event.stopPropagation(); setVoidTarget(row); }}
                            className="flex h-11 w-11 items-center justify-center rounded-lg lg:h-8 lg:w-8 text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                            title="Void"
                        >
                            <FiXCircle className="h-4 w-4" />
                        </button>
                    )}
                </div>
            )
        }
    ];

    return (
        <div className="page mx-auto w-full gap-2 lg:max-w-7xl">

            {/* Header */}
            <div className="flex shrink-0 flex-col justify-between gap-2 rounded-2xl p-2
                            sm:flex-row sm:items-center sm:gap-4 sm:p-3"
                style={{ background: 'linear-gradient(135deg, #05650fff 0%, #158f21ff 100%)' }}
            >
                <div>
                    <h1 className="text-lg font-black tracking-tight text-white sm:text-2xl">Orders</h1>
                    <p className="mt-1 hidden text-sm font-medium text-white/70 sm:block">
                        Every ticket, and what happened to it
                    </p>
                </div>
                <button
                    type="button"
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200/80
                               bg-white px-4 py-3 text-sm font-bold text-slate-700 shadow-sm transition-colors
                               hover:bg-slate-50 active:bg-slate-100 sm:w-auto lg:py-2"
                    onClick={() => refetch()}
                >
                    <FiRefreshCw className="h-4 w-4 text-slate-400" />
                    Refresh
                </button>
            </div>

            {/* Filters */}
            <div className="flex shrink-0 flex-col gap-2 rounded-2xl border border-slate-200/80 bg-white p-2
                            shadow-sm sm:flex-row sm:flex-wrap sm:items-end sm:gap-3">
                <div className="relative w-full sm:min-w-[16rem] sm:flex-1">
                    <FiSearch className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                        className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-4 text-sm font-semibold text-slate-800 outline-none transition-all placeholder:font-medium placeholder:text-slate-400 focus:border-brand-500 focus:bg-white focus:ring-4 focus:ring-brand-500/20 sm:h-10"
                        placeholder="Search orders, customers, or FBR numbers"
                        value={filters.search}
                        onChange={(event) => setFilters({ ...filters, search: event.target.value, page: 1 })}
                    />
                </div>

                <div className="flex w-full items-center gap-2 sm:w-auto">
                    <input
                        type="date"
                        className="h-11 w-full min-w-0 cursor-pointer rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm outline-none transition-all focus:border-brand-500 focus:ring-4 focus:ring-brand-500/20 sm:w-36 lg:h-10"
                        value={filters.fromDate}
                        onChange={(event) => setFilters({ ...filters, fromDate: event.target.value, page: 1 })}
                    />
                    <span className="text-slate-300 font-bold text-sm">to</span>
                    <input
                        type="date"
                        className="h-11 w-full min-w-0 cursor-pointer rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm outline-none transition-all focus:border-brand-500 focus:ring-4 focus:ring-brand-500/20 sm:h-10 sm:w-36"
                        value={filters.toDate}
                        onChange={(event) => setFilters({ ...filters, toDate: event.target.value, page: 1 })}
                    />
                </div>

                <div className="flex w-full items-center gap-2 sm:w-auto">
                    <select
                        className="h-11 w-full min-w-0 flex-1 cursor-pointer rounded-xl border border-slate-200 bg-white px-3 pr-8 text-sm font-semibold text-slate-700 shadow-sm outline-none transition-all focus:border-brand-500 focus:ring-4 focus:ring-brand-500/20 sm:h-10 sm:w-auto sm:flex-none"
                        value={filters.statusCode}
                        onChange={(event) => setFilters({ ...filters, statusCode: event.target.value, page: 1 })}
                    >
                        <option value="">All statuses</option>
                        {orderStatuses.map((status) => (
                            <option key={status.Order_Status_Code} value={status.Order_Status_Code}>
                                {status.Order_Status_Name}
                            </option>
                        ))}
                    </select>

                    <select
                        className="h-11 w-full min-w-0 flex-1 cursor-pointer rounded-xl border border-slate-200 bg-white px-3 pr-8 text-sm font-semibold text-slate-700 shadow-sm outline-none transition-all focus:border-brand-500 focus:ring-4 focus:ring-brand-500/20 sm:h-10 sm:w-auto sm:flex-none"
                        value={filters.orderTypeCode}
                        onChange={(event) => setFilters({ ...filters, orderTypeCode: event.target.value, page: 1 })}
                    >
                        <option value="">All types</option>
                        {orderTypes.map((type) => (
                            <option key={type.Order_Type_Code} value={type.Order_Type_Code}>
                                {type.Order_Type_Name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border-slate-200/80
                            sm:border sm:bg-white sm:shadow-sm">
                <DataTable
                    className="flex-1"
                    columns={columns}
                    rows={orders || []}
                    loading={loading}
                    meta={meta}
                    onPageChange={(page) => setFilters({ ...filters, page })}
                    rowKey={(row) => row.Order_ID}
                    onRowClick={(row) => (row.Is_Held || !row.Invoice_Number ? resume(row) : openReceipt(row.Order_ID))}
                    rowClassName={(row) => `group cursor-pointer hover:bg-slate-50 transition-colors ${row.Order_Status_Code === 'VOID' ? 'opacity-60 saturate-50' : ''}`}
                    emptyTitle="No orders in this range"
                    emptyDescription="Adjust your filters to see more results."
                    emptyIcon={FiFileText}
                />
            </div>

            {/* Receipt preview - the same component that prints, at real size */}
            <Modal
                open={!!receipt}
                onClose={() => setReceipt(null)}
                title="View Receipt"
                width="sm"
                footer={(
                    <>
                        <button
                            type="button"
                            className="rounded-xl px-4 py-2 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-100"
                            onClick={() => setReceipt(null)}
                        >
                            Close
                        </button>
                        <button
                            type="button"
                            className="flex items-center gap-2 rounded-xl bg-brand-600 px-5 py-2 text-sm font-bold text-white shadow-sm transition-colors hover:bg-brand-700"
                            onClick={async () => {
                                await printReceipt(receipt, { copies: 1 });
                                // Recording the reprint is useful but not worth
                                // failing the print over.
                                await orderApi.confirmPrint(receipt.invoice.orderId).catch(() => { });
                            }}
                        >
                            <FiPrinter className="h-4 w-4" /> Print Receipt
                        </button>
                    </>
                )}
            >
                <div className="flex justify-center bg-slate-100 p-4 rounded-xl shadow-inner">
                    <Receipt receipt={receipt} isPreview />
                </div>
            </Modal>

            {/* Void Modal */}
            <Modal
                open={!!voidTarget}
                onClose={() => setVoidTarget(null)}
                title={<span className="text-xl font-black text-slate-800">Void {voidTarget?.Order_Number || ''}?</span>}
                subtitle={<span className="text-red-500 font-bold">Rs. {money(voidTarget?.Total_Payable || 0)}</span>}
                width="sm"
                dismissible={!voiding}
                footer={(
                    <>
                        <button
                            type="button"
                            className="rounded-xl px-4 py-2 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-100"
                            onClick={() => setVoidTarget(null)}
                            disabled={voiding}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="flex items-center gap-2 rounded-xl bg-red-500 px-5 py-2 text-sm font-bold text-white shadow-sm transition-colors hover:bg-red-600 disabled:opacity-50"
                            onClick={() => doVoid(voidTarget.Order_ID)}
                            disabled={voiding || !voidReasonId}
                        >
                            {voiding ? 'Voiding…' : 'Confirm Void'}
                        </button>
                    </>
                )}
            >
                <div className="space-y-4 pt-2">
                    <div className="rounded-xl bg-amber-50 p-4 border border-amber-100">
                        <p className="text-xs font-semibold leading-relaxed text-amber-800 mt-1">
                            <strong className="text-amber-900 block mb-1">Warning:</strong>
                            Any stock consumed by this order will be returned to inventory. The invoice is withdrawn from the queue if it has not yet been accepted by authorities.
                        </p>
                    </div>

                    <Field label="Reason" required>
                        <Select
                            value={voidReasonId}
                            onChange={(e) => setVoidReasonId(e.target.value)}
                            className="rounded-xl border-slate-200 focus:border-red-500 focus:ring-red-500/20"
                        >
                            <option value="">Select a reason…</option>
                            {voidReasons.map((reason) => (
                                <option key={reason.Void_Reason_ID} value={reason.Void_Reason_ID}>
                                    {reason.Void_Reason_Name}
                                </option>
                            ))}
                        </Select>
                    </Field>

                    <Field label="Remarks">
                        <Textarea
                            rows={3}
                            value={voidRemarks}
                            onChange={(e) => setVoidRemarks(e.target.value)}
                            placeholder="Add additional context for future audits..."
                            className="rounded-xl border-slate-200 resize-none focus:border-red-500 focus:ring-red-500/20"
                        />
                    </Field>
                </div>
            </Modal>
        </div>
    );
};

export default OrdersPage;
